import { useState, useEffect, useRef } from "react";
import { listSessions, deleteSession, updateSession, getDeveloperProjects, listRealms } from "../lib/api";
import type { ChatSession } from "../types";
import type { ViewType } from "../App";
import type { AuthUser } from "../lib/auth";

interface SidebarProps {
  user: AuthUser;
  view: ViewType;
  setView: (view: ViewType) => void;
  collapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
  currentSessionId: string | null;
  onSelectSession: (id: string | null) => void;
  activeRealmId: string | null;
  onRealmChange: (id: string | null) => void;
  realms: any[];
}

export function Sidebar({
  user,
  view,
  setView,
  collapsed,
  onToggle,
  onLogout,
  currentSessionId,
  onSelectSession,
  activeRealmId,
  onRealmChange,
  realms,
}: SidebarProps) {
  const activeRealm = realms.find(r => r.id === activeRealmId) || realms[0] || null;
  const [quickSessions, setQuickSessions] = useState<ChatSession[]>([]);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [expandedWorkspace, setExpandedWorkspace] = useState<string | null>(null);
  const [workspaceSessions, setWorkspaceSessions] = useState<Record<string, ChatSession[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadWorkspaces(); }, []);

  const loadWorkspaces = async () => {
    try {
      const [sessionData, projectData] = await Promise.all([
        listSessions(),
        getDeveloperProjects().catch(() => ({ projects: [] })),
      ]);
      const allSessions = (sessionData.sessions || []) as ChatSession[];
      const projects = projectData.projects || [];

      // Split: sessions with project_id vs without
      const quick = allSessions.filter((s: any) => !s.project_id && s.message_count > 0).slice(0, 5);
      setQuickSessions(quick);

      // Group sessions by project_id
      const grouped: Record<string, ChatSession[]> = {};
      for (const s of allSessions) {
        const pid = (s as any).project_id;
        if (pid && (s as any).message_count > 0) {
          if (!grouped[pid]) grouped[pid] = [];
          grouped[pid].push(s);
        }
      }
      setWorkspaceSessions(grouped);

      // Sort: projects with repos first (dev), then life projects
      const sorted = projects.sort((a: any, b: any) => {
        const aHasRepos = (a.repos?.length || 0) > 0 ? 0 : 1;
        const bHasRepos = (b.repos?.length || 0) > 0 ? 0 : 1;
        return aHasRepos - bHasRepos || a.name.localeCompare(b.name);
      });
      setWorkspaces(sorted);
    } catch {} finally { setLoading(false); }
  };

  const handleNewChat = (projectId?: string) => {
    // Store desired project for the next session creation
    if (projectId) {
      sessionStorage.setItem("pending_project_id", projectId);
    } else {
      sessionStorage.removeItem("pending_project_id");
    }
    onSelectSession(null);
    setView("chat");
  };

  const handleSelectSession = (session: ChatSession) => {
    onSelectSession(session.id);
    setView("chat");
  };

  const handleDeleteSession = async (id: string) => {
    await deleteSession(id).catch(() => {});
    setQuickSessions(prev => prev.filter(s => s.id !== id));
    setWorkspaceSessions(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = updated[key].filter(s => s.id !== id);
      }
      return updated;
    });
    if (currentSessionId === id) onSelectSession(null);
  };

  const isAdmin = user.role === "platform_admin" || user.role === "admin";

  return (
    <aside
      className={`
        h-full flex flex-col bg-sidebar border-r border-sidebar-border
        transition-all duration-300 ease-in-out
        ${collapsed ? "w-16" : "w-70"}
      `}
    >
      {/* Header + Realm Switcher */}
      <div className="border-b border-sidebar-border">
        <div className="flex items-center justify-between p-3">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-sm font-bold text-white">AI</span>
              </div>
              <span className="font-semibold text-foreground">AI App</span>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-card text-muted-foreground hover:text-foreground transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <ChevronLeftIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {/* Realm Switcher */}
        {!collapsed && realms.length > 0 && (
          <div className="px-3 pb-3">
            <select
              value={activeRealmId || ""}
              onChange={(e) => onRealmChange(e.target.value || null)}
              className="w-full bg-card border border-border rounded-lg px-3 py-1.5 text-sm text-foreground appearance-none cursor-pointer"
              style={{ backgroundImage: activeRealm?.color ? `linear-gradient(to right, ${activeRealm.color}20, transparent)` : undefined }}
            >
              {realms.map((r: any) => (
                <option key={r.id} value={r.id}>
                  {r.icon || (r.type === "personal" ? "🏠" : "🏢")} {r.name}
                </option>
              ))}
              <option value="">All Realms</option>
            </select>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* New Chat Button */}
        <div className="p-2">
          <button
            onClick={() => handleNewChat()}
            className={`
              w-full flex items-center gap-2 rounded-lg font-medium
              bg-primary/10 hover:bg-primary/20 text-primary transition-colors
              ${collapsed ? "justify-center p-2" : "px-3 py-2"}
            `}
            title="New Chat"
          >
            <PlusIcon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>New Chat</span>}
          </button>
        </div>

        {!collapsed && !loading && (
          <>
            {/* Quick Chats (no project) */}
            {quickSessions.length > 0 && (
              <div className="px-2 mb-2">
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent</div>
                <div className="space-y-0.5">
                  {quickSessions.map((session) => (
                    <SessionRow key={session.id} session={session} active={currentSessionId === session.id} onSelect={handleSelectSession} onDelete={handleDeleteSession} />
                  ))}
                </div>
              </div>
            )}

            {/* Workspaces */}
            {workspaces.length > 0 && (
              <div className="px-2">
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">Workspaces</div>
                <div className="space-y-0.5">
                  {workspaces.map((ws: any) => {
                    const isExpanded = expandedWorkspace === ws.id;
                    const wsSessions = (workspaceSessions[ws.id] || []).slice(0, 5);
                    const hasRepos = (ws.repos?.length || 0) > 0;
                    return (
                      <div key={ws.id}>
                        <button
                          onClick={() => setExpandedWorkspace(isExpanded ? null : ws.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-card transition-colors text-left"
                        >
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ws.color || "#6b7280" }} />
                          <span className="flex-1 truncate text-foreground font-medium">{ws.name}</span>
                          {wsSessions.length > 0 && (
                            <span className="text-[10px] text-muted-foreground bg-card border border-border rounded px-1">{wsSessions.length}</span>
                          )}
                          <span className={`text-muted-foreground text-xs transition-transform ${isExpanded ? "rotate-90" : ""}`}>▶</span>
                        </button>
                        {isExpanded && (
                          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-2">
                            <button
                              onClick={() => handleNewChat(ws.id)}
                              className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-xs text-primary hover:bg-primary/10 transition-colors"
                            >
                              <PlusIcon className="w-3.5 h-3.5" /> New Chat
                            </button>
                            {wsSessions.map((session) => (
                              <SessionRow key={session.id} session={session} active={currentSessionId === session.id} onSelect={handleSelectSession} onDelete={handleDeleteSession} compact />
                            ))}
                            {hasRepos && (
                              <div className="flex flex-wrap gap-1 px-2 py-1">
                                {ws.repos.map((r: any) => (
                                  <span key={r.name} className={`px-1.5 py-0.5 text-[9px] rounded ${r.is_primary ? "bg-primary/15 text-primary" : "bg-card border border-border text-muted-foreground"}`}>
                                    {r.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Navigation Sections */}
        <div className={`mt-4 ${collapsed ? "px-1" : "px-2"}`}>
          {!collapsed && (
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Productivity
            </div>
          )}
          <NavItem
            icon={<TasksIcon className="w-5 h-5" />}
            label="Tasks"
            active={view === "tasks"}
            collapsed={collapsed}
            onClick={() => setView("tasks")}
          />
          <NavItem
            icon={<IntegrationsIcon className="w-5 h-5" />}
            label="Integrations"
            active={view === "integrations"}
            collapsed={collapsed}
            onClick={() => setView("integrations")}
          />
          <NavItem
            icon={<SkillsIcon className="w-5 h-5" />}
            label="Skills"
            active={view === "skills"}
            collapsed={collapsed}
            onClick={() => setView("skills")}
          />

          {isAdmin && (
            <>
              {!collapsed && (
                <div className="px-2 py-1 mt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Admin
                </div>
              )}
              <NavItem
                icon={<AdminIcon className="w-5 h-5" />}
                label="Admin Panel"
                active={view === "admin"}
                collapsed={collapsed}
                onClick={() => setView("admin")}
              />
            </>
          )}

          {!collapsed && (
            <div className="px-2 py-1 mt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Tools
            </div>
          )}
          <NavItem
            icon={<ModelsIcon className="w-5 h-5" />}
            label="Model Compare"
            active={view === "models"}
            collapsed={collapsed}
            onClick={() => setView("models")}
          />
          <NavItem
            icon={<SettingsIcon className="w-5 h-5" />}
            label="Settings"
            active={view === "settings"}
            collapsed={collapsed}
            onClick={() => setView("settings")}
          />
        </div>
      </div>

      {/* User Section */}
      <div className="border-t border-sidebar-border p-2">
        <div
          className={`
            flex items-center gap-2 rounded-lg p-2
            ${collapsed ? "justify-center" : ""}
          `}
        >
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-primary">
              {(user.name || user.email)?.[0]?.toUpperCase() || "?"}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {user.name || user.email}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground truncate">{user.role}</span>
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={onLogout}
              className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive transition-colors"
              title="Sign out"
            >
              <LogoutIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

function SessionRow({ session, active, onSelect, onDelete, compact }: {
  session: ChatSession;
  active: boolean;
  onSelect: (s: ChatSession) => void;
  onDelete: (id: string) => void;
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(session.title || "");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSaveTitle = async () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== session.title) {
      await updateSession(session.id, { title: trimmed }).catch(() => {});
      session.title = trimmed; // optimistic update
    }
    setEditing(false);
  };

  return (
    <div className={`group flex items-center gap-1 rounded-lg ${compact ? "text-xs" : "text-sm"} transition-colors ${active ? "bg-card text-foreground" : "text-muted-foreground hover:bg-card hover:text-foreground"}`}>
      {editing ? (
        <input
          ref={inputRef}
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={handleSaveTitle}
          onKeyDown={(e) => { if (e.key === "Enter") handleSaveTitle(); if (e.key === "Escape") setEditing(false); }}
          autoFocus
          className="flex-1 px-2 py-1 bg-background border border-primary rounded text-xs text-foreground min-w-0"
        />
      ) : (
        <button
          onClick={() => onSelect(session)}
          onDoubleClick={(e) => { e.stopPropagation(); setEditTitle(session.title || ""); setEditing(true); }}
          className="flex-1 text-left px-2 py-1.5 min-w-0"
          title={`${session.title || "Untitled"} (double-click to rename)`}
        >
          <div className="truncate">{session.title || "Untitled"}</div>
          {!compact && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <span>{session.message_count} msgs</span>
              {session.total_cost_usd > 0 && <span className="font-mono">${(session.total_cost_usd ?? 0).toFixed(4)}</span>}
            </div>
          )}
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
        className="opacity-0 group-hover:opacity-100 p-1 mr-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all flex-shrink-0"
        title="Remove"
      >
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
}

function NavItem({
  icon,
  label,
  active,
  collapsed,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 rounded-lg transition-colors
        ${collapsed ? "justify-center p-2" : "px-3 py-2"}
        ${
          active
            ? "bg-card text-foreground"
            : "text-muted-foreground hover:bg-card hover:text-foreground"
        }
      `}
      title={label}
    >
      {icon}
      {!collapsed && <span className="text-sm">{label}</span>}
    </button>
  );
}

// Icons
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TasksIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  );
}

function SkillsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IntegrationsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function AdminIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v1H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3V5a3 3 0 0 0-3-3z" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="16" y2="16" />
    </svg>
  );
}

function ModelsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
